import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Check, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  getScientificPalettes,
  getSingleColorPalettes,
  getDivergingPalettes,
  getPalettePreview,
  getDefaultPalette,
  getPaletteColors,
  mapIntervalToColorIndex
} from '../../../utils/colorPalettes';
import { datasetApi } from '../../../api/datasetApi';
import {
  MAP_NO_METRIC,
  metricColumnFromSpec,
  isNumericColumn,
  defaultAggregationForColumn
} from '../../../utils/mapLayerMetricUtils';
import {
  CLASSIFICATION_METHODS,
  MIN_NUM_CLASSES,
  MAX_NUM_CLASSES,
  DEFAULT_NUM_CLASSES,
  computeAutoClassBreaks,
  computeDiscreteColorBands,
  formatValue,
  getLayerNumClasses,
  layerSupportsClassification,
  extractRetrievedPropertyKeys,
  inferPropertyTypeFromLayer,
  layerHasColorableProperty,
  normalizeClassBreaks
} from '../../../utils/mapUtils';
import { getDatasetSchema, setDatasetSchema } from '../../../utils/datasetSchemaCache';

const PALETTE_CATEGORIES = [
  { id: 'scientific', label: 'Scientific', getPalettes: getScientificPalettes },
  { id: 'diverging', label: 'Diverging', getPalettes: getDivergingPalettes },
  { id: 'sequential', label: 'Sequential', getPalettes: getSingleColorPalettes }
];

const categoryForPalette = (paletteName) => {
  for (const category of PALETTE_CATEGORIES) {
    if (category.getPalettes().includes(paletteName)) return category.id;
  }
  return 'scientific';
};

const ColorPicker = ({
  layer,
  readOnly = false,
  schemaColumns: schemaColumnsProp,
  onClose,
  onUpdateTheme,
  onUpdateLayerClassification,
  onUpdateLayerColorProperty,
  onUpdateLayerMetric
}) => {
  const pickerRef = useRef(null);
  const currentPalette = layer.config?.palette || getDefaultPalette();
  const isInverted = layer.config?.inverted || false;
  const [paletteCategory, setPaletteCategory] = useState(() => categoryForPalette(currentPalette));

  useEffect(() => {
    setPaletteCategory(categoryForPalette(currentPalette));
  }, [currentPalette]);

  const datasetId = layer.query?.dataset || layer.dataset;
  const hasLoadedData = Boolean(layer.data?.features?.length);
  const canColorFromData = hasLoadedData && onUpdateLayerColorProperty;
  const canReloadFromDataset = !readOnly && Boolean(datasetId && onUpdateLayerMetric);

  const [fetchedSchemaColumns, setFetchedSchemaColumns] = useState([]);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const schemaColumns = schemaColumnsProp?.length ? schemaColumnsProp : fetchedSchemaColumns;
  const [aggregation, setAggregation] = useState(layer.query?.value?.aggregation || 'sum');
  const [applying, setApplying] = useState(false);

  const retrievedKeys = useMemo(
    () => extractRetrievedPropertyKeys(layer),
    [layer]
  );

  const activeColorColumn = layer.config?.uniformColor
    ? MAP_NO_METRIC
    : (layer.config?.colorProperty || metricColumnFromSpec(layer.query?.value) || 'value');

  const reloadColumnOptions = useMemo(() => {
    if (!canReloadFromDataset) return [];
    const retrievedSet = new Set(retrievedKeys);
    return schemaColumns
      .map((c) => c.name)
      .filter((name) => !retrievedSet.has(name));
  }, [canReloadFromDataset, schemaColumns, retrievedKeys]);

  const showAggregation = canReloadFromDataset
    && activeColorColumn
    && activeColorColumn !== MAP_NO_METRIC
    && !layerHasColorableProperty(layer, activeColorColumn);

  const metricIsNumeric = activeColorColumn
    && activeColorColumn !== MAP_NO_METRIC
    && (schemaColumns.length
      ? isNumericColumn(activeColorColumn, schemaColumns)
      : inferPropertyTypeFromLayer(layer, activeColorColumn) === 'numeric');

  useEffect(() => {
    setAggregation(layer.query?.value?.aggregation || 'sum');
  }, [layer.id, layer.query?.value?.aggregation]);

  useEffect(() => {
    if (!canReloadFromDataset || schemaColumnsProp?.length) {
      setFetchedSchemaColumns([]);
      setLoadingSchema(false);
      return;
    }
    const cached = getDatasetSchema(datasetId);
    if (cached) {
      setFetchedSchemaColumns(cached);
      return;
    }
    let cancelled = false;
    setLoadingSchema(true);
    datasetApi.getDatasetMetadata(datasetId)
      .then((meta) => {
        if (cancelled) return;
        const cols = meta.schema || [];
        setDatasetSchema(datasetId, cols);
        setFetchedSchemaColumns(cols);
      })
      .catch((err) => console.error('Failed to load dataset schema for color picker:', err))
      .finally(() => {
        if (!cancelled) setLoadingSchema(false);
      });
    return () => { cancelled = true; };
  }, [canReloadFromDataset, datasetId, schemaColumnsProp]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-active-trigger="true"]')) return;
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handlePaletteSelect = (paletteName) => {
    onUpdateTheme(layer.id, null, paletteName, isInverted);
  };

  const handleInvertToggle = () => {
    const paletteToUse = currentPalette || getDefaultPalette();
    onUpdateTheme(layer.id, null, paletteToUse, !isInverted);
  };

  const applyColorColumn = async (col) => {
    if (col === activeColorColumn) return;

    setApplying(true);
    try {
      if (col === MAP_NO_METRIC) {
        if (canColorFromData) {
          onUpdateLayerColorProperty(layer.id, MAP_NO_METRIC);
        } else if (canReloadFromDataset) {
          await onUpdateLayerMetric(layer.id, MAP_NO_METRIC, 'count');
        }
        return;
      }

      if (canColorFromData && layerHasColorableProperty(layer, col)) {
        onUpdateLayerColorProperty(layer.id, col);
        return;
      }

      if (canReloadFromDataset) {
        const nextAgg = defaultAggregationForColumn(col, schemaColumns, aggregation);
        setAggregation(nextAgg);
        await onUpdateLayerMetric(layer.id, col, nextAgg);
      }
    } finally {
      setApplying(false);
    }
  };

  const handleAggregationChange = async (agg) => {
    setAggregation(agg);
    if (!canReloadFromDataset || !activeColorColumn || activeColorColumn === MAP_NO_METRIC) return;
    if (layerHasColorableProperty(layer, activeColorColumn)) return;
    setApplying(true);
    try {
      await onUpdateLayerMetric(layer.id, activeColorColumn, agg);
    } finally {
      setApplying(false);
    }
  };

  const propertyLabel = (key) => {
    if (schemaColumns.length && !isNumericColumn(key, schemaColumns)) {
      return `${key} (category)`;
    }
    if (inferPropertyTypeFromLayer(layer, key) === 'categorical') {
      return `${key} (category)`;
    }
    return key;
  };

  const activePaletteCategory = PALETTE_CATEGORIES.find((c) => c.id === paletteCategory) || PALETTE_CATEGORIES[0];
  const visiblePalettes = activePaletteCategory.getPalettes();
  const showColorSection = canColorFromData || canReloadFromDataset;
  const showClassification = layerSupportsClassification(layer) && onUpdateLayerClassification;
  const classificationMethod = layer.config?.classificationMethod || CLASSIFICATION_METHODS.QUANTILE;
  const numClasses = getLayerNumClasses(layer);
  const dataMin = layer.stats?.min ?? 0;
  const dataMax = layer.stats?.max ?? 100;

  const currentBreaks = useMemo(() => {
    if (!showClassification) return [];
    if (classificationMethod === CLASSIFICATION_METHODS.MANUAL && layer.config?.classBreaks?.length >= 2) {
      return layer.config.classBreaks;
    }
    return computeAutoClassBreaks(
      { ...layer, config: { ...layer.config, classificationMethod: CLASSIFICATION_METHODS.QUANTILE } },
      numClasses
    );
  }, [showClassification, classificationMethod, layer, numClasses]);

  const handleClassificationMethodChange = (method) => {
    if (method === CLASSIFICATION_METHODS.MANUAL) {
      const breaks = computeAutoClassBreaks(layer, numClasses);
      onUpdateLayerClassification(layer.id, {
        classificationMethod: method,
        numClasses,
        classBreaks: breaks
      });
      return;
    }
    onUpdateLayerClassification(layer.id, { classificationMethod: method, numClasses });
  };

  const handleNumClassesChange = (nextNum) => {
    const parsed = Number(nextNum);
    if (classificationMethod === CLASSIFICATION_METHODS.MANUAL) {
      onUpdateLayerClassification(layer.id, {
        classificationMethod,
        numClasses: parsed,
        classBreaks: computeAutoClassBreaks(
          { ...layer, config: { ...layer.config, classificationMethod: CLASSIFICATION_METHODS.QUANTILE } },
          parsed
        )
      });
      return;
    }
    onUpdateLayerClassification(layer.id, { classificationMethod, numClasses: parsed });
  };

  const manualColorBands = useMemo(() => {
    if (!showClassification || classificationMethod !== CLASSIFICATION_METHODS.MANUAL) {
      return [];
    }
    const previewLayer = {
      ...layer,
      config: {
        ...layer.config,
        classificationMethod: CLASSIFICATION_METHODS.MANUAL,
        classBreaks: currentBreaks,
        numClasses
      }
    };
    return computeDiscreteColorBands(
      previewLayer,
      getPaletteColors,
      mapIntervalToColorIndex,
      getDefaultPalette,
      numClasses
    ).colorBands || [];
  }, [showClassification, classificationMethod, layer, currentBreaks, numClasses]);

  const commitClassUpperBound = (bandIndex, rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;

    const breakIndex = bandIndex + 1;
    if (breakIndex <= 0 || breakIndex >= currentBreaks.length - 1) return;

    const span = dataMax - dataMin;
    const eps = span > 0 ? span / (currentBreaks.length * 1e6) : 1e-9;
    const breaks = [...currentBreaks];

    // Apply the edit, then push neighboring thresholds so class count is kept
    // even when the new value crosses previously set levels.
    breaks[breakIndex] = Math.min(Math.max(parsed, dataMin + eps), dataMax - eps);

    for (let i = breakIndex + 1; i < breaks.length - 1; i++) {
      if (breaks[i] <= breaks[i - 1]) {
        breaks[i] = breaks[i - 1] + eps;
      }
    }
    for (let i = breakIndex - 1; i >= 1; i--) {
      if (breaks[i] >= breaks[i + 1]) {
        breaks[i] = breaks[i + 1] - eps;
      }
    }

    const nextBreaks = normalizeClassBreaks(breaks, dataMin, dataMax);
    if (
      nextBreaks.length === currentBreaks.length &&
      nextBreaks.every((v, i) => v === currentBreaks[i])
    ) {
      return;
    }

    onUpdateLayerClassification(layer.id, {
      classificationMethod: CLASSIFICATION_METHODS.MANUAL,
      numClasses,
      classBreaks: nextBreaks
    });
  };

  const handleResetClassification = () => {
    onUpdateLayerClassification(layer.id, {
      classificationMethod: CLASSIFICATION_METHODS.QUANTILE,
      numClasses: DEFAULT_NUM_CLASSES
    });
  };

  return (
    <div
      ref={pickerRef}
      className="absolute left-[calc(100%+12px)] top-0 z-[100] w-72 bg-white dark:bg-gray-900 p-4 rounded-[24px] shadow-2xl border border-gray-100 dark:border-gray-800 animate-in fade-in slide-in-from-left-2 duration-200 max-h-[80vh] overflow-y-auto"
    >
      <div className="flex justify-end -mt-1 mb-2">
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          aria-label="Close color picker"
        >
          <Plus size={14} className="rotate-45" />
        </button>
      </div>

      {showColorSection && (
        <div className="mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
          <h6 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest mb-2">
            Color by
          </h6>
          <div className="flex gap-2">
            <select
              value={activeColorColumn}
              disabled={applying || layer.status === 'loading' || (!retrievedKeys.length && !reloadColumnOptions.length)}
              onChange={(e) => applyColorColumn(e.target.value)}
              className="flex-1 min-w-0 text-xs py-2 px-2.5 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value={MAP_NO_METRIC}>No metric (uniform color)</option>
              {retrievedKeys.length > 0 && (
                <optgroup label="In current layer">
                  {retrievedKeys.map((key) => (
                    <option key={key} value={key}>
                      {propertyLabel(key)}
                    </option>
                  ))}
                </optgroup>
              )}
              {reloadColumnOptions.length > 0 && (
                <optgroup label="Reload from dataset">
                  {reloadColumnOptions.map((name) => (
                    <option key={`reload-${name}`} value={name}>
                      {propertyLabel(name)}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {showAggregation && (
              <select
                value={aggregation}
                disabled={applying || loadingSchema || layer.status === 'loading'}
                onChange={(e) => handleAggregationChange(e.target.value)}
                className="w-[88px] shrink-0 text-[10px] font-bold py-2 px-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 uppercase tracking-wider text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
              >
                {metricIsNumeric && (
                  <>
                    <option value="sum">SUM</option>
                    <option value="avg">AVG</option>
                  </>
                )}
                <option value="min">MIN</option>
                <option value="max">MAX</option>
                <option value="count">COUNT</option>
              </select>
            )}
          </div>
          {(loadingSchema || applying) && (
            <div className="flex items-center gap-1.5 mt-2 text-[9px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-widest">
              <Loader2 size={12} className="animate-spin" />
              {applying ? 'Updating layer...' : 'Loading dataset columns...'}
            </div>
          )}
          {layer.config?.highCardinalityCategorical && (
            <p className="mt-2 text-[8px] text-amber-600 dark:text-amber-400 font-medium leading-snug">
              Too many categories to color meaningfully — features use a neutral fill.
            </p>
          )}
          {retrievedKeys.length > 0 && !layer.config?.highCardinalityCategorical && (
            <p className="mt-2 text-[8px] text-gray-500 dark:text-gray-400 font-medium leading-snug">
              Columns in the current layer update instantly. Other dataset columns reload the map.
            </p>
          )}
        </div>
      )}

      {showClassification && (
        <div className="mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <h6 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest">
              Classification
            </h6>
            <button
              type="button"
              onClick={handleResetClassification}
              className="text-[8px] font-bold uppercase tracking-wider text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
            >
              Reset
            </button>
          </div>
          <div className="flex gap-2 mb-2">
            <select
              value={classificationMethod}
              onChange={(e) => handleClassificationMethodChange(e.target.value)}
              className="flex-1 min-w-0 text-xs py-2 px-2.5 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value={CLASSIFICATION_METHODS.QUANTILE}>Quantile (equal count)</option>
              <option value={CLASSIFICATION_METHODS.EQUAL_INTERVAL}>Equal interval</option>
              <option value={CLASSIFICATION_METHODS.MANUAL}>Custom ranges</option>
            </select>
            <select
              value={numClasses}
              onChange={(e) => handleNumClassesChange(e.target.value)}
              className="w-[72px] shrink-0 text-xs py-2 px-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
              title="Number of classes"
            >
              {Array.from({ length: MAX_NUM_CLASSES - MIN_NUM_CLASSES + 1 }, (_, i) => MIN_NUM_CLASSES + i).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {classificationMethod === CLASSIFICATION_METHODS.MANUAL && manualColorBands.length > 0 && (
            <div className="space-y-2">
              <p className="text-[8px] text-gray-500 dark:text-gray-400 font-medium leading-snug">
                Set where each color class ends. Matches the legend below the layer.
              </p>
              <div className="grid grid-cols-[14px_minmax(0,1fr)_72px] gap-x-2 gap-y-1.5 max-h-44 overflow-y-auto pr-1 items-center">
                {manualColorBands.map((band, index) => {
                  const isLast = index === manualColorBands.length - 1;
                  const isFirst = index === 0;
                  const inputClass = cn(
                    'w-full text-[10px] py-1 px-1.5 rounded-lg bg-white dark:bg-gray-950',
                    'border border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white',
                    'tabular-nums outline-none focus:ring-2 focus:ring-emerald-500/30 text-right'
                  );

                  return (
                    <React.Fragment key={index}>
                      <div
                        className="w-3.5 h-3.5 rounded border border-gray-300 dark:border-gray-600"
                        style={{ backgroundColor: band.color }}
                        title={`Class ${index + 1}`}
                      />
                      <span className="text-[9px] text-gray-500 dark:text-gray-400 text-right tabular-nums truncate">
                        {isLast ? '' : isFirst ? '<' : `${formatValue(band.value)} –`}
                      </span>
                      {isLast ? (
                        <span className="text-[9px] font-medium text-gray-600 dark:text-gray-300 tabular-nums text-right">
                          ≥ {formatValue(band.value)}
                        </span>
                      ) : (
                        <input
                          key={`${index}-${band.nextValue}`}
                          type="text"
                          inputMode="decimal"
                          defaultValue={band.nextValue}
                          onBlur={(e) => commitClassUpperBound(index, e.target.value)}
                          className={inputClass}
                          aria-label={`Class ${index + 1} upper limit`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="flex justify-between text-[8px] text-gray-400 dark:text-gray-500 font-medium tabular-nums">
                <span>Min {formatValue(dataMin)}</span>
                <span>Max {formatValue(dataMax)}</span>
              </div>
            </div>
          )}
          {classificationMethod !== CLASSIFICATION_METHODS.MANUAL && (
            <p className="text-[8px] text-gray-500 dark:text-gray-400 font-medium leading-snug">
              Quantile puts the same number of features in each class; equal interval uses fixed value ranges.
            </p>
          )}
        </div>
      )}

      <div className="mb-3">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <h6 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest shrink-0">
            Select Palette
          </h6>
          <select
            value={paletteCategory}
            onChange={(e) => setPaletteCategory(e.target.value)}
            className="min-w-0 flex-1 text-[10px] py-1.5 px-2 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
            aria-label="Palette category"
          >
            {PALETTE_CATEGORIES.map((category) => (
              <option key={category.id} value={category.id}>{category.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[8px] text-gray-500 dark:text-gray-400 font-medium">
            {isInverted ? 'Palettes shown inverted (reversed)' : 'Click invert to reverse color order'}
          </p>
          <button
            onClick={handleInvertToggle}
            className={cn(
              "px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider",
              isInverted
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            )}
            title="Invert all palettes (reverse color order from low to high)"
          >
            <RotateCcw size={12} />
            <span>Invert</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {visiblePalettes.map((paletteName) => {
          const isSelected = currentPalette === paletteName;
          return (
            <button
              key={paletteName}
              onClick={() => handlePaletteSelect(paletteName)}
              className={cn(
                "relative rounded-lg transition-all overflow-hidden group",
                isSelected
                  ? "ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-gray-900 scale-105"
                  : "hover:scale-105 ring-1 ring-gray-200 dark:ring-gray-700"
              )}
              style={{
                background: getPalettePreview(paletteName, isInverted),
                height: '40px'
              }}
              title={paletteName.charAt(0).toUpperCase() + paletteName.slice(1)}
            >
              {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <Check size={16} className="text-white drop-shadow-lg" strokeWidth={3} />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-1.5">
                <span className="text-[8px] font-bold text-white uppercase tracking-wider drop-shadow">
                  {paletteName}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ColorPicker;
